import { ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, AuthCode, Credentials, DomainLink, DomainSettings, formatDate, formatPrice, PhpInfo, Product, ServiceDetail, SftpInfo } from '../../api';
import { Alert, CredentialsBox, Loading, StatusTag, useAction, useLoad } from '../../Components/ui';
import type { TabProps } from './ControlPanel';

function Kv({ label, children }: { label: string; children: ReactNode }) {
    return <div><div className="kv-label">{label}</div><div className="kv-value">{children}</div></div>;
}

/** GET/POST /services/:id/sftp — CarbonEdge B2B API. */
function SftpCard({ base }: { base: string }) {
    const { data: sftp, error, loading } = useLoad(() => api.get<{ sftp: SftpInfo }>(`${base}/sftp`).then(r => r.data.sftp), [base]);
    const [credentials, setCredentials] = useState<Credentials | null>(null);
    const action = useAction();

    return (
        <div className="panel-card">
            <h3>SFTP access</h3>
            {error && <Alert type="error">{error}</Alert>}
            {action.error && <Alert type="error">{action.error}</Alert>}
            {loading && !sftp ? <Loading /> : sftp && (
                <>
                    <div className="kv-grid" style={{ marginBottom: '1rem' }}>
                        <Kv label="Host"><span className="mono">{sftp.host}</span></Kv>
                        <Kv label="Port"><span className="mono">{sftp.port}</span></Kv>
                        <Kv label="Username"><span className="mono">{sftp.username}</span></Kv>
                        {sftp.rootPath && <Kv label="Web root"><span className="mono">{sftp.rootPath}</span></Kv>}
                    </div>
                    {credentials ? <CredentialsBox credentials={credentials} onClose={() => setCredentials(null)} /> : (
                        <button className="btn-outline small" disabled={action.busy}
                            onClick={() => action.run(async () => setCredentials((await api.post<{ credentials: Credentials }>(`${base}/sftp/reset-password`)).data.credentials))}>
                            Generate new SFTP password
                        </button>
                    )}
                </>
            )}
        </div>
    );
}

/** Extended feature (domainSettings): nameservers and auto renew. Not in the CarbonEdge B2B API yet. */
function DomainSettingsCard({ base }: { base: string }) {
    const { data: domain, setData, error, loading } = useLoad(() => api.get<{ domain: DomainSettings }>(`${base}/domain`).then(r => r.data.domain), [base]);
    const [nameservers, setNameservers] = useState<string | null>(null);
    const { busy, error: actionError, run } = useAction();

    if (loading && !domain) return <div className="panel-card"><Loading /></div>;
    if (error || !domain) return <Alert type="error">{error ?? 'Could not load the domain'}</Alert>;

    const update = (path: string, body: unknown) => run(async () => setData((await api.put<{ domain: DomainSettings }>(`${base}/domain/${path}`, body)).data.domain));

    return (
        <div className="panel-card">
            <h3>Domain</h3>
            {actionError && <Alert type="error">{actionError}</Alert>}
            <div className="kv-grid" style={{ marginBottom: '1.25rem' }}>
                {domain.status && <Kv label="Status">{domain.status}</Kv>}
                {domain.expiresAt && <Kv label="Expires">{formatDate(domain.expiresAt)}</Kv>}
                <Kv label="Auto renew">{domain.autoRenew ? 'On' : 'Off'}</Kv>
                {domain.dnssec !== null && <Kv label="DNSSEC">{domain.dnssec ? 'Enabled' : 'Disabled'}</Kv>}
            </div>
            <div className="btn-row" style={{ marginBottom: '1.25rem' }}>
                <button className="btn-outline small" disabled={busy} onClick={() => update('auto-renew', { enabled: !domain.autoRenew })}>
                    {domain.autoRenew ? 'Turn off auto renew' : 'Turn on auto renew'}
                </button>
            </div>
            <form className="fi-form" onSubmit={e => {
                e.preventDefault();
                void update('nameservers', { nameservers: (nameservers ?? domain.nameservers.join('\n')).split(/\s+/).filter(Boolean) });
            }}>
                <label>
                    Nameservers
                    <textarea rows={3} value={nameservers ?? domain.nameservers.join('\n')} onChange={e => setNameservers(e.target.value)} className="mono" />
                    <span className="hint">One per line. Keep our nameservers to manage DNS from this panel.</span>
                </label>
                <button className="btn-outline small" disabled={busy}>Save nameservers</button>
            </form>
        </div>
    );
}

/**
 * Transfer out (extended feature domainTransfer or domainSettings): unlock the domain and reveal its
 * auth code, which the new registrar asks for. Not in the CarbonEdge B2B API yet.
 */
function TransferOutCard({ base }: { base: string }) {
    const { data: domain, setData, error, loading } = useLoad(() => api.get<{ domain: DomainSettings }>(`${base}/domain`).then(r => r.data.domain), [base]);
    const [authCode, setAuthCode] = useState<AuthCode | null>(null);
    const { busy, error: actionError, run } = useAction();

    if (loading && !domain) return <div className="panel-card"><Loading /></div>;
    if (error || !domain) return <Alert type="error">{error ?? 'Could not load the domain'}</Alert>;

    const setLock = (locked: boolean) => run(async () => {
        setData((await api.put<{ domain: DomainSettings }>(`${base}/domain/lock`, { enabled: locked })).data.domain);
        if (locked) setAuthCode(null);
    });

    return (
        <div className="panel-card">
            <h3>Transfer domain away</h3>
            {actionError && <Alert type="error">{actionError}</Alert>}
            <p className="card-text" style={{ marginBottom: '1rem' }}>
                To move <strong>{domain.name}</strong> to another registrar, get its auth code (EPP code) here — this also unlocks the domain — and give the code to the new registrar.
                Keep the code secret — anyone with it can transfer your domain.
            </p>
            <div className="kv-grid" style={{ marginBottom: '1.25rem' }}>
                <Kv label="Transfer lock">{domain.locked ? '🔒 Locked' : '🔓 Unlocked'}</Kv>
                {domain.expiresAt && <Kv label="Expires">{formatDate(domain.expiresAt)}</Kv>}
            </div>
            <div className="btn-row">
                <button className="btn-submit inline small" disabled={busy}
                    onClick={() => run(async () => {
                        // CarbonEdge unlocks the domain when the auth code is generated.
                        setAuthCode((await api.get<{ authCode: AuthCode }>(`${base}/domain/auth-code`)).data.authCode);
                        setData({ ...domain, locked: false });
                    })}>
                    Get auth code (unlocks the domain)
                </button>
                {!domain.locked && (
                    <button className="btn-outline small" disabled={busy} onClick={() => setLock(true)}>Lock domain again</button>
                )}
            </div>
            {authCode && (
                <div className="credentials-box" style={{ marginTop: '1rem' }}>
                    <p>Give this code to your new registrar. Lock the domain again if you change your mind.</p>
                    <div className="cred-row">
                        <span>Auth code</span><span className="mono">{authCode.authCode}</span>
                        <button className="link-button" onClick={() => navigator.clipboard?.writeText(authCode.authCode)}>Copy</button>
                    </div>
                    {authCode.validUntil && <div className="cred-row"><span>Valid until</span><span>{formatDate(authCode.validUntil)}</span></div>}
                </div>
            )}
        </div>
    );
}

/** On a domain the customer owns: the hosting using it, or plans to add hosting directly on it. */
function WebHostingCard({ detail }: { detail: ServiceDetail }) {
    const navigate = useNavigate();
    const { service, linkedHosting } = detail;
    const { data: plans, loading } = useLoad(
        () => (linkedHosting ? Promise.resolve([] as Product[])
            : api.get<{ products: Product[] }>('/products').then(r => r.data.products.filter(p => p.category === 'wordpress' || p.category === 'static'))),
        [linkedHosting?.id]);

    return (
        <div className="panel-card">
            <h3>Web hosting</h3>
            {linkedHosting ? (
                <div className="btn-row">
                    <span className="card-text">{service.domain} is hosted on <strong>{linkedHosting.productName}</strong></span>
                    <StatusTag status={linkedHosting.status} />
                    <Link className="btn-outline small" to={`/panel/${linkedHosting.id}`}>Open hosting panel</Link>
                </div>
            ) : (
                <>
                    <p className="card-text" style={{ marginBottom: '1rem' }}>
                        Put a website on <strong>{service.domain}</strong>. Pick a package — the domain is filled in for you and
                        pointed at the new site automatically when it is ready.
                    </p>
                    {loading && !plans ? <Loading /> : (
                        <div className="table-wrap">
                            <table className="data-table">
                                <tbody>
                                    {plans?.map(p => (
                                        <tr key={p.slug}>
                                            <td><strong>{p.name}</strong><div className="card-text" style={{ fontSize: '0.8rem' }}>{p.tagline}</div></td>
                                            <td>{formatPrice(p.priceOre, p.billingPeriod)}</td>
                                            <td className="actions">
                                                <button className="btn-submit inline small" onClick={() => navigate(`/checkout/${p.slug}?domainService=${service.id}`)}>
                                                    Add to {service.domain}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

/** On hosting that uses an owned domain: which domain, and how its DNS was pointed at the site. */
function LinkedDomainCard({ base, detail }: { base: string; detail: ServiceDetail }) {
    const [link, setLink] = useState<DomainLink | null>(detail.service.domainLink);
    const { busy, error, run } = useAction();
    const domain = detail.linkedDomain!;

    return (
        <div className="panel-card">
            <div className="panel-card-header">
                <h3>Domain</h3>
                <Link className="btn-outline small" to={`/panel/${domain.id}`}>Open domain</Link>
            </div>
            <p className="card-text" style={{ marginBottom: '1rem' }}>
                This hosting uses your domain <strong>{domain.domain}</strong>, registered with Forget-IT. Its DNS is pointed at this site automatically.
            </p>
            {error && <Alert type="error">{error}</Alert>}
            {link?.error && <Alert type="error">Pointing the domain failed: {link.error}</Alert>}
            {link && link.conflicts.length > 0 && (
                <Alert type="info">
                    These existing records on {domain.domain} point elsewhere and were left unchanged: <span className="mono">{link.conflicts.join(', ')}</span>.
                    {detail.capabilities.dnsEdit
                        ? " Change or delete them in the domain's DNS tab, then point the domain again."
                        : ' Contact us and we will point the domain at this site for you.'}
                </Alert>
            )}
            {link && !link.error && link.conflicts.length === 0 && (
                <Alert type="success">
                    {domain.domain} points at this site{link.applied.length ? ` (${link.applied.join(', ')})` : ''}.
                </Alert>
            )}
            {!link && <p className="card-text" style={{ marginBottom: '1rem' }}>The domain is pointed here as soon as the hosting is active.</p>}
            <button className="btn-outline small" disabled={busy}
                onClick={() => run(async () => setLink((await api.post<{ domainLink: DomainLink }>(`${base}/point-domain`)).data.domainLink))}>
                Point domain again
            </button>
        </div>
    );
}

const DESCRIBE_SPEC: Record<string, (v: string | number | boolean) => string> = {
    php: v => `PHP ${v}`,
    storage_gb: v => `${v} GB storage`,
    ssl: v => (v ? 'SSL' : 'No SSL'),
    db_type: v => `${v === 'postgresql' ? 'PostgreSQL' : 'MySQL'} database`,
    backup_daily: v => (v ? 'Daily backups' : 'No daily backups'),
};

export default function OverviewTab({ base, detail }: TabProps) {
    const { service, carbonEdge, capabilities } = detail;
    const hasSite = service.type === 'wordpress' || service.type === 'static';
    const php = useLoad<PhpInfo | null>(
        () => (capabilities.php && hasSite ? api.get<{ php: PhpInfo }>(`${base}/php`).then(r => r.data.php) : Promise.resolve(null)),
        [base, capabilities.php, hasSite]);
    const { busy, error, run } = useAction();
    const nameservers = carbonEdge?.meta.nameservers.length ? carbonEdge.meta.nameservers : service.nameservers;

    return (
        <>
            <div className="panel-card">
                <h3>Service</h3>
                {error && <Alert type="error">{error}</Alert>}
                <div className="kv-grid">
                    <Kv label="Product">{service.productName}</Kv>
                    <Kv label="Domain">{service.domain}</Kv>
                    <Kv label="Created">{formatDate(service.createdAt)}</Kv>
                    <Kv label="Renews">{formatDate(service.expiresAt)}</Kv>
                    {carbonEdge && (
                        <Kv label="Plan">
                            {Object.entries(carbonEdge.specs).filter(([k]) => DESCRIBE_SPEC[k]).map(([k, v]) => DESCRIBE_SPEC[k](v)).join(' · ') || '—'}
                        </Kv>
                    )}
                    {php.data && (
                        <Kv label="PHP version">
                            <div className="fi-form"><select value={php.data.currentVersion} disabled={busy}
                                onChange={e => run(async () => { php.setData((await api.put<{ php: PhpInfo }>(`${base}/php`, { version: e.target.value })).data.php); })}>
                                {php.data.availableVersions.map(v => <option key={v} value={v}>PHP {v}</option>)}
                            </select></div>
                            {php.data.memoryLimit && <span className="hint" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Memory {php.data.memoryLimit}{php.data.opcacheEnabled ? ' · OPcache on' : ''}</span>}
                        </Kv>
                    )}
                </div>
            </div>

            {service.type === 'domain' && <WebHostingCard detail={detail} />}

            {hasSite && detail.linkedDomain && <LinkedDomainCard base={base} detail={detail} />}

            {hasSite && <SftpCard base={base} />}

            {capabilities.domainSettings && <DomainSettingsCard base={base} />}

            {service.type === 'domain' && (capabilities.domainTransfer || capabilities.domainSettings) && <TransferOutCard base={base} />}

            {/* Domains registered or transferred through us already use our nameservers. */}
            {(nameservers.length > 0 || detail.webIps.length > 0) && service.type !== 'domain' && !detail.linkedDomain && (
                <div className="panel-card">
                    <h3>Point your domain to us</h3>
                    {nameservers.length > 0 && (
                        <>
                            <p className="card-text">
                                If <strong>{service.domain}</strong> is registered elsewhere, set these nameservers at your registrar. Changes can take up to 24 hours to propagate.
                            </p>
                            <ul className="contact-list" style={{ marginTop: '1rem' }}>
                                {nameservers.map(ns => <li key={ns} className="mono">{ns}</li>)}
                            </ul>
                        </>
                    )}
                    {detail.webIps.length > 0 && (
                        <>
                            <p className="card-text" style={{ marginTop: nameservers.length ? '1.25rem' : 0 }}>
                                {nameservers.length ? 'Or keep your DNS where it is' : 'At your DNS provider,'} and add these A records. The site is served from {detail.webIps.length > 1 ? 'all of them' : 'it'}, so add every one:
                            </p>
                            <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
                                <table className="data-table">
                                    <thead><tr><th>Type</th><th>Name</th><th>Value</th></tr></thead>
                                    <tbody>
                                        {['@', 'www'].flatMap(name => detail.webIps.map(ip => (
                                            <tr key={`${name}-${ip}`}><td>A</td><td className="mono">{name}</td><td className="mono">{ip}</td></tr>
                                        )))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
}
